import { useState } from 'react'
import { X, Plus, Minus, CreditCard } from 'lucide-react'
import { Button, Input } from '../UI'

const CreditAdjustModal = ({ isOpen, onClose, onSubmit, userName, currentBalance }) => {
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [isAdding, setIsAdding] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const numAmount = parseInt(amount)
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const adjustedAmount = isAdding ? numAmount : -numAmount
      await onSubmit(adjustedAmount, reason)
      onClose()
      setAmount('')
      setReason('')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const previewBalance = (() => {
    const numAmount = parseInt(amount) || 0
    const adjusted = isAdding ? numAmount : -numAmount
    return Math.max(0, currentBalance + adjusted)
  })()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-dark-900 rounded-xl border border-dark-700 w-full max-w-md p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-dark-400 hover:text-dark-200 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent-500/20 rounded-lg">
            <CreditCard className="w-5 h-5 text-accent-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Adjust Credits</h3>
            <p className="text-sm text-dark-400">{userName}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Add/Remove Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
                isAdding
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-dark-800 text-dark-400 border border-transparent hover:bg-dark-700'
              }`}
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
                !isAdding
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'bg-dark-800 text-dark-400 border border-transparent hover:bg-dark-700'
              }`}
            >
              <Minus className="w-4 h-4" />
              Remove
            </button>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Amount</label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter credit amount"
              min="1"
              required
            />
          </div>

          {/* Reason Input */}
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Reason (optional)</label>
            <Input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you adjusting credits?"
            />
          </div>

          {/* Balance Preview */}
          <div className="bg-dark-800 rounded-lg p-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-dark-400">Current Balance:</span>
              <span className="font-mono">{currentBalance.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-dark-400">After Adjustment:</span>
              <span className={`font-mono font-semibold ${previewBalance > currentBalance ? 'text-green-400' : previewBalance < currentBalance ? 'text-red-400' : ''}`}>
                {previewBalance.toLocaleString()}
              </span>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreditAdjustModal
